import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios"; // ✅ Fix: Import axios
import MyContext from "../Context/MyContext";
import "./Profile.scss";

const Profile = () => {
  const {
    token,
    usertoken,
    fetchusertoken,
    setLoader,
    setMessage,
    setOpen,
    url,
  } = useContext(MyContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      navigate("/login");
    } else {
      fetchusertoken();
    }
  }, [token]);

  // ✅ Validation Schema using Yup
  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),

    domain: Yup.string()
      // .oneOf(["mern stack", "cyber-security", "data analysis", "python", "ML/AI"], "Invalid domain selection")
      .required("Domain is required"), // ✅ Restrict domain selection

    role: Yup.string().required("Project role is required"),
    studentId: Yup.string().required("Student ID is required"),

    dob: Yup.date()
      .max(new Date(), "Date of Birth cannot be in the future") // ✅ Ensure valid past date
      .required("Date of Birth is required"),

    university: Yup.string().required("University is required"),

    linkedin: Yup.string()
      .url("Invalid LinkedIn URL")
      .matches(
        /(https?:\/\/)?(www\.)?linkedin\.com\/.*$/,
        "Invalid LinkedIn URL"
      ) // ✅ Ensures LinkedIn URL is correct
      .required("LinkedIn is required"),

    internshipStart: Yup.date()
      .required("Start date is required")
      .typeError("Invalid start date"), // ✅ Ensure valid date format

    internshipEnd: Yup.date()
      .required("End date is required")
      .min(Yup.ref("internshipStart"), "End date must be after start date") // ✅ Ensure end date is after start date
      .typeError("Invalid end date"),

    internshipStatus: Yup.string().required("Internship status is required"),
  });

  // ✅ Handle Form Submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoader(true);
    document.body.style.overflow = "hidden";

    try {
      const { data } = await axios.post(`${url}/profile-info`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (data.success) {
        setMessage(data.message);

        fetchusertoken();
      } else {
        setMessage(data.error || "Failed to update profile");
      }
      setOpen(true);
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage("Something went wrong. Please try again.");
      setOpen(true);
    } finally {
      setLoader(false);
      document.body.style.overflow = "auto";
      setSubmitting(false);
    }
  };

  return (
    <div className="profile-container">
      <h2 className="head">User Profile</h2>

      <div className="modal">
        <div className="modal-content">
          <Formik
            enableReinitialize // ✅ Ensures updated values after fetching user data
            initialValues={{
              name: usertoken?.name || "",
              email: usertoken?.email || "",
              domain: usertoken?.domain || "",
              role: usertoken?.project || "",
              studentId: usertoken?.studentId || "",
              dob: usertoken?.dob || "",
              university: usertoken?.university || "",
              linkedin: usertoken?.linkedin || "",
              internshipStart: usertoken?.internshipStartDate || "",
              internshipEnd: usertoken?.internshipEndDate || "",
              internshipStatus: usertoken?.internshipStatus || "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form className="profile-form">
                <label>Name:</label>
                <Field type="text" name="name" />
                <ErrorMessage name="name" component="div" className="error" />

                <label>Email:</label>
                <Field type="email" name="email" disabled />
                <ErrorMessage name="email" component="div" className="error" />

                <label>Domain:</label>
                <Field as="select" name="domain">
                  <option value="" selected disabled>
                    Select Domain
                  </option>
                  <option value="mern-stack">MERN Stack</option>
                  <option value="cyber-security">Cyber Security</option>
                  <option value="data-analysis">Data Analysis</option>
                  <option value="python">Python</option>
                  <option value="ai-ml">AI/ML</option>
                  <option value="full-stack">Full Stack</option>
                  <option value="app-development">App Development</option>
                   <option value="data-science">Data Science</option>
                </Field>
                <ErrorMessage name="domain" component="div" className="error" />

                <label>Project Role:</label>
                <Field type="text" name="role" />
                <ErrorMessage name="role" component="div" className="error" />

                <label>Student ID:</label>
                <Field type="text" name="studentId" />
                <ErrorMessage
                  name="studentId"
                  component="div"
                  className="error"
                />

                <label>Date of Birth:</label>
                <Field type="date" name="dob" />
                <ErrorMessage name="dob" component="div" className="error" />

                <label>University:</label>
                <Field type="text" name="university" />
                <ErrorMessage
                  name="university"
                  component="div"
                  className="error"
                />

                <label>LinkedIn Profile:</label>
                <Field type="text" name="linkedin" />
                <ErrorMessage
                  name="linkedin"
                  component="div"
                  className="error"
                />

                <label>Internship Start Date:</label>
                <Field type="date" name="internshipStart" />
                <ErrorMessage
                  name="internshipStart"
                  component="div"
                  className="error"
                />

                <label>Internship End Date:</label>
                <Field type="date" name="internshipEnd" />
                <ErrorMessage
                  name="internshipEnd"
                  component="div"
                  className="error"
                />

                <label>Internship Status:</label>
                <Field type="text" name="internshipStatus" disabled />
                <ErrorMessage
                  name="internshipStatus"
                  component="div"
                  className="error"
                />

                <div className="button-group">
                  <button type="submit" disabled={isSubmitting}>
                    Save Changes
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default Profile;
